import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import { useInventoryBrand } from '../../hooks/useInventoryBrand';
import { excludeCompanyLevel } from '../../utils/brandFilters';
import {
  Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, TextField,
  InputAdornment, FormControl, InputLabel, Select, MenuItem, Button,
  TablePagination, Stack, Paper, Tooltip, IconButton
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon,
  AccountTree as BomIcon, Add as AddIcon
} from '@mui/icons-material';
import bomService from '../../services/bomService';
import ProductionBomEditorDialog from './ProductionBomEditorDialog';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

// Color-code the bom_type chip
function bomTypeChip(bomType) {
  const map = {
    production: { color: 'primary', label: 'Production' },
    sales_kit: { color: 'secondary', label: 'Sales Kit' },
    unknown: { color: 'default', label: 'Unknown' },
  };
  const cfg = map[bomType] || { color: 'default', label: bomType || '—' };
  return <Chip size="small" color={cfg.color} label={cfg.label} variant="outlined" />;
}

// Format a BOM's cost as a single value or a range. Cost varies by warehouse, so
// the backend returns min/max over warehouses where the roll-up is complete; null
// means no complete cost data (don't show a fabricated number).
function formatCogs(r) {
  if (r.cogs_min == null || r.cogs_max == null) return '—';
  const cur = r.cogs_currency && r.cogs_currency !== 'USD' ? `${r.cogs_currency} ` : '$';
  const fmt = (n) => `${cur}${Number(n).toFixed(2)}`;
  return r.cogs_min === r.cogs_max ? fmt(r.cogs_min) : `${fmt(r.cogs_min)} - ${fmt(r.cogs_max)}`;
}

export function BomCatalogPanel({ embedded = false }) {
  const { selectedBrand, brands, selectBrand } = useInventoryBrand();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [masterOnly, setMasterOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // production BOM creator (shared dialog)
  const [editorOpen, setEditorOpen] = useState(false);

  const fetchBoms = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        page_size: pageSize,
      };
      if (search) params.search = search;
      if (sourceFilter) params.source = sourceFilter;
      if (typeFilter) params.bom_type = typeFilter;
      if (masterOnly) params.master_only = '1';
      const data = await bomService.listBoms(selectedBrand.id, params);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to load BOMs'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id, page, pageSize, search, sourceFilter, typeFilter, masterOnly]);

  useEffect(() => { fetchBoms(); }, [fetchBoms]);

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A BOM's home is its assembly's product page (Products & COGS → BOM tab),
  // where recipes are shown with costs per geography and can be edited.
  const openBomDetail = (assemblySku) => {
    navigate(`/inventory/cogs?product=${encodeURIComponent(assemblySku)}&ptab=3`);
  };

  if (!selectedBrand?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Select a brand to view its BOM catalog.</Alert></Box>;
  }

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        {!embedded && (
          <>
            <BomIcon color="primary" />
            <Box>
              <Typography variant="h5">BOM Catalog</Typography>
              <Typography variant="body2" color="text.secondary">
                Production recipes &amp; sales kits — click a row to view and edit it on the product page
              </Typography>
            </Box>
          </>
        )}
        {embedded && (
          <Typography variant="body2" color="text.secondary">
            Production recipes &amp; sales kits — click a row to view and edit it on the product page
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {!embedded && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Select Brand</InputLabel>
            <Select
              value={selectedBrand?.id || ''}
              label="Select Brand"
              onChange={(e) => {
                const brand = brands.find((b) => b.id === e.target.value);
                if (brand) { selectBrand(brand); setPage(0); }
              }}
            >
              {excludeCompanyLevel(brands).map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setEditorOpen(true)}>
          New Production BOM
        </Button>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchBoms}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Search assembly SKU"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 240 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All types</MenuItem>
                <MenuItem value="sales_kit">Sales Kit</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Source</InputLabel>
              <Select value={sourceFilter} label="Source" onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All sources</MenuItem>
                <MenuItem value="manual">manual</MenuItem>
                <MenuItem value="cin7">cin7</MenuItem>
                <MenuItem value="auto-import-l2">auto-import-l2</MenuItem>
                <MenuItem value="manual-import-20251208">manual-import-20251208</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Master default</InputLabel>
              <Select value={masterOnly ? '1' : ''} label="Master default" onChange={(e) => { setMasterOnly(e.target.value === '1'); setPage(0); }}>
                <MenuItem value="">All alternatives</MenuItem>
                <MenuItem value="1">Master only</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Assembly SKU</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Master</TableCell>
                <TableCell align="center">Components</TableCell>
                <TableCell>Geographies</TableCell>
                <TableCell align="right">COGS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No BOMs found.</Typography></TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openBomDetail(r.assembly_sku)}
                >
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.assembly_sku}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{r.name || '—'}</Typography></TableCell>
                  <TableCell>{bomTypeChip(r.bom_type)}</TableCell>
                  <TableCell align="center">{r.is_master_default ? '✓' : ''}</TableCell>
                  <TableCell align="center">{r.component_count}</TableCell>
                  <TableCell>
                    {(r.geographies || []).map((g) => (
                      <Chip key={g} size="small" label={g} sx={{ mr: 0.5, mb: 0.25 }} variant="outlined" />
                    ))}
                  </TableCell>
                  <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{formatCogs(r)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      <ProductionBomEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        brandId={selectedBrand?.id}
        mode="create"
        onSaved={() => fetchBoms()}
      />
    </Box>
  );
}


// Standalone page (route /inventory/boms — no longer in the nav; the BOMs tab
// inside Products & COGS is the primary home).
export default function BomCatalog() {
  return <BomCatalogPanel />;
}
